import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FaFilePdf } from 'react-icons/fa';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import { normalizeIngredientName, formatAmount, shouldExcludeIngredient } from '@/utils/ingredientDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// 2026-02 · Botón para descargar el plan de alimentación Fit Evolution en PDF.
// Datos: /api/personalized-plans/fit-evolution/recipes/{email} → batches[].recipes[]
// Cada `recipe` dentro de un batch es una Opción para esa comida.
export default function FitEvolutionDownloadButton({ userEmail, userName, planNameFallback, batches: batchesProp }) {
  const [downloading, setDownloading] = useState(false);

  // 2026-02 · Filtro estricto (regla del usuario): sólo alimentos principales.
  // Excluye condimentos, salsas, aceites <10g, cualquier ingrediente <10g,
  // opcionales / al gusto y vegetales <30g. Ver `shouldExcludeIngredient`.
  const extractIngredientEntries = (recipe) => {
    const ings = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
    const out = [];
    ings.forEach(it => {
      const rawName = it?.name;
      const amount = it?.amount;
      if (!rawName) return;
      if (shouldExcludeIngredient(rawName, amount)) return;
      const name = normalizeIngredientName(rawName);
      const displayAmount = formatAmount(amount);
      out.push({ name, amount: displayAmount });
    });
    return out;
  };

  const handleDownload = async () => {
    if (!userEmail) {
      toast.error('No se encontró el email de la usuaria');
      return;
    }
    setDownloading(true);
    try {
      let batches = batchesProp;
      if (!batches || batches.length === 0) {
        const { data } = await axios.get(
          `${API}/api/personalized-plans/fit-evolution/recipes/${encodeURIComponent(userEmail)}`
        );
        batches = data?.batches || [];
      }
      if (!batches || batches.length === 0) {
        toast.error('Aún no hay un plan de alimentación disponible');
        return;
      }

      // Ordenar por meal_position si existe
      const sorted = batches.slice().sort((a, b) => {
        const pa = a.meal_position || 0;
        const pb = b.meal_position || 0;
        if (pa && pb) return pa - pb;
        return String(a.meal_time || '').localeCompare(String(b.meal_time || ''));
      });

      const planName = planNameFallback || 'Mi Plan Fit Evolution';
      const clientName = userName || 'Cliente';

      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 15;
      let y = margin;

      // Encabezado
      doc.setFillColor(212, 28, 112);
      doc.rect(0, 0, pageW, 22, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(planName, margin, 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(clientName, margin, 17);
      y = 30;
      doc.setTextColor(30, 30, 30);

      // Nota introductoria (texto exacto 2026-02, sin emojis: label bold + texto)
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text('Cómo seguir tu plan', margin, y);
      y += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const paragraph = 'Tienes 6 opciones por comida, elige la que más se adapte a ti. Todas tienen macronutrientes similares.';
      const wrapPara = doc.splitTextToSize(paragraph, pageW - margin * 2);
      wrapPara.forEach(w => { doc.text(w, margin, y); y += 4.5; });
      y += 2;
      doc.text('Para que tus cantidades sean correctas, ten en cuenta:', margin, y);
      y += 5;
      const items = [
        { label: 'Proteínas', text: 'pollo, carne y pescado se pesan en crudo, antes de cocinar.' },
        { label: 'Proteína en polvo', text: 'se pesa tal cual viene en polvo, antes de mezclarla.' },
        { label: 'Carbohidratos', text: 'arroz, papa, plátano y similares se pesan cocidos.' },
        { label: 'Harinas y cereales', text: 'avena, crema de arroz, harinas y cereales se pesan en seco, antes de cocinar.' },
        { label: 'Granos y legumbres', text: 'fríjoles, lentejas, garbanzos y similares se pesan en crudo/seco, salvo que tu plan indique específicamente que la cantidad corresponde al alimento cocido.' }
      ];
      const bulletX = margin;
      const contentX = margin + 4;
      items.forEach(({ label, text }) => {
        // Bullet negro
        doc.setFillColor(212, 28, 112);
        doc.circle(bulletX + 1.2, y - 1.4, 0.9, 'F');
        doc.setTextColor(30, 30, 30);
        doc.setFont('helvetica', 'bold');
        const labelText = `${label}: `;
        doc.text(labelText, contentX, y);
        const labelWidth = doc.getTextWidth(labelText);
        doc.setFont('helvetica', 'normal');
        const remainingWidth = pageW - margin - (contentX + labelWidth);
        const firstLineWrap = doc.splitTextToSize(text, remainingWidth);
        if (firstLineWrap.length > 0) {
          doc.text(firstLineWrap[0], contentX + labelWidth, y);
          y += 4.5;
          if (firstLineWrap.length > 1) {
            // Reflow continuación con ancho completo
            const rest = text.substring(firstLineWrap[0].length).trim();
            const wrap = doc.splitTextToSize(rest, pageW - margin - contentX);
            wrap.forEach(w => { doc.text(w, contentX, y); y += 4.5; });
          }
        } else {
          y += 4.5;
        }
        y += 1.5;
      });
      y += 4;

      const COLS_PER_ROW = 3;
      const colGap = 4;
      const rowGap = 6;

      sorted.forEach((batch, batchIdx) => {
        const recipes = Array.isArray(batch.recipes) ? batch.recipes : [];
        if (recipes.length === 0) return;

        const rawTitle = batch.meal_time || `Comida ${batchIdx + 1}`;
        const mealTitle = batch.is_post_workout ? `${rawTitle} · Post-Entreno` : rawTitle;

        if (y > pageH - 40) {
          doc.addPage();
          y = margin;
        }

        doc.setFillColor(245, 235, 245);
        doc.rect(margin - 2, y - 4, pageW - margin * 2 + 4, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(212, 28, 112);
        doc.text(mealTitle, margin, y);
        y += 6;

        for (let start = 0; start < recipes.length; start += COLS_PER_ROW) {
          const rowRecipes = recipes.slice(start, start + COLS_PER_ROW);
          const cols = rowRecipes.length;
          const colW = (pageW - margin * 2 - colGap * (cols - 1)) / cols;

          // 2026-02 · Filtro estricto (sólo alimentos principales) y cantidad en
          // negrita entre paréntesis. Cada renglón puede llevar segmentos con
          // distinta fuente (nombre normal + cantidad bold).
          const rowLines = rowRecipes.map(r => {
            const items = extractIngredientEntries(r);
            const segments = [];
            if (items.length === 0) {
              segments.push([{ text: '(sin ingredientes principales)', bold: false }]);
              return segments;
            }
            items.forEach(({ name, amount }) => {
              const bulletName = `• ${name}`;
              const amountText = amount ? ` (${amount})` : '';
              doc.setFontSize(8.5);
              doc.setFont('helvetica', 'normal');
              const nameWidth = doc.getTextWidth(bulletName);
              doc.setFont('helvetica', 'bold');
              const amtWidth = amountText ? doc.getTextWidth(amountText) : 0;
              doc.setFont('helvetica', 'normal');
              if (nameWidth + amtWidth <= colW - 4) {
                segments.push([
                  { text: bulletName, bold: false },
                  ...(amountText ? [{ text: amountText, bold: true }] : []),
                ]);
              } else {
                const wrapped = doc.splitTextToSize(bulletName, colW - 4);
                wrapped.forEach(w => segments.push([{ text: w, bold: false }]));
                if (amountText) {
                  segments.push([{ text: `  (${amount})`, bold: true }]);
                }
              }
            });
            return segments;
          });
          const maxLines = Math.max(...rowLines.map(l => l.length));
          const rowH = 8 + maxLines * 4.2 + 4;

          if (y + rowH > pageH - margin) {
            doc.addPage();
            y = margin;
          }

          rowRecipes.forEach((_, cIdx) => {
            const x = margin + cIdx * (colW + colGap);
            doc.setDrawColor(220, 220, 230);
            doc.setFillColor(252, 252, 255);
            doc.rect(x, y, colW, rowH - 2, 'FD');
            // Header opción
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(70, 70, 90);
            doc.text(`Opción ${start + cIdx + 1}`, x + 2, y + 5);

            let ly = y + 10;
            rowLines[cIdx].forEach((segs) => {
              let sx = x + 2;
              segs.forEach(({ text, bold }) => {
                doc.setFont('helvetica', bold ? 'bold' : 'normal');
                doc.setFontSize(8.5);
                doc.setTextColor(30, 30, 30);
                doc.text(text, sx, ly);
                sx += doc.getTextWidth(text);
              });
              ly += 4.2;
            });
          });
          y += rowH;
        }
        y += rowGap;
      });

      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 160);
        doc.text(`${p} / ${totalPages}`, pageW - margin, pageH - 8);
      }

      const safeName = String(clientName).replace(/[^a-zA-Z0-9]+/g, '_');
      doc.save(`plan_alimentacion_${safeName}.pdf`);
      toast.success('PDF descargado');
    } catch (err) {
      console.error('Error al generar PDF:', err);
      toast.error('No se pudo generar el PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={downloading}
      className="text-white font-semibold px-5 py-2 rounded-full uppercase tracking-wider text-xs"
      style={{ background: 'linear-gradient(135deg, var(--brand-primary), #D41C70)' }}
      data-testid="fit-evo-download-plan-btn"
    >
      {downloading ? 'Generando PDF...' : (<><FaFilePdf className="mr-2" /> Descargar mi plan</>)}
    </Button>
  );
}
